import React, { useState, useEffect } from 'react';
import {
  Card,
  Checkbox,
  Button,
  Spin,
  Alert,
  Tag,
  Row,
  Col,
  Space,
  Divider,
  Typography,
  message
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  SecurityScanOutlined
} from '@ant-design/icons';
import { useGetUserInfo } from 'hooks/useGetUserInfo';
import { useTeamPermissions } from '../hooks/useTeamPermissions';
import { END_POINT } from 'config/environment';
import { useQueryClient } from 'react-query';
import { QUERY_KEY_FOR_TEAM_MEMBERS } from '../hooks/useGetMembers';

const { Title, Text } = Typography;

interface TeamPermissionManagerProps {
  userId: string;
  onSuccess?: () => void;
}

// Permission groups based on your requirements
const permissionGroups = [
  {
    key: 'jobs',
    label: 'Job Management',
    description: 'Manage job postings and listings',
    permissions: [
      { key: 'job.create', label: 'Create Jobs', description: 'Can create new job postings' },
      { key: 'job.view', label: 'View Jobs', description: 'Can view all jobs' },
      { key: 'job.edit', label: 'Edit Jobs', description: 'Can edit existing jobs' },
      { key: 'job.viewapplicants', label: 'View Applicants', description: 'Can view applicants for jobs' },
      { key: 'job.delete', label: 'Delete Jobs', description: 'Can delete existing jobs' },
    ]
  },
  {
    key: 'talents',
    label: 'Talent Management',
    description: 'Access and manage talent profiles',
    permissions: [
      { key: 'talent.view', label: 'View Talents', description: 'Can view talent profiles' },
    ]
  },
  {
    key: 'smartscreen',
    label: 'SmartScreen',
    description: 'Use the AI screening tools',
    permissions: [
      { key: 'smartscreen.access', label: 'Access SmartScreen', description: 'Can use SmartScreen features' },
    ]
  },
  {
    key: 'invites',
    label: 'Invite Management',
    description: 'Manage job and talent invites',
    permissions: [
      { key: 'invite.create', label: 'Create Invites', description: 'Can create job invites' },
      { key: 'invite.view', label: 'View Invites', description: 'Can view all invites' },
      { key: 'invite.accept', label: 'Accept Invites', description: 'Can accept job invites' },
      { key: 'invite.delete', label: 'Delete Invites', description: 'Can delete invites' },
      { key: 'invite.decline', label: 'Decline Invites', description: 'Can decline invites' },
    ]
  },
  
  {
    key: 'folders',
    label: 'Folder Management',
    description: 'Organize talents in folders',
    permissions: [
      { key: 'folder.create', label: 'Create Folders', description: 'Can create new folders' },
      { key: 'folder.view', label: 'View Folders', description: 'Can view all folders' },
      { key: 'folder.delete', label: 'Delete Folders', description: 'Can delete folders' },
    ]
  },
  {
    key: 'messages',
    label: 'Messaging',
    description: 'Communicate with talents',
    permissions: [
      { key: 'message.view', label: 'View Messages', description: 'Can view chat messages' },
    ]
  }
];

export const TeamPermissionManager: React.FC<TeamPermissionManagerProps> = ({
  userId,
  onSuccess
}) => {
  const { token } = useGetUserInfo();
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [originalPermissions, setOriginalPermissions] = useState<string[]>([]); // Track original permissions
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: availablePermissions,
    isLoading: loadingPermissions,
    isError: loadError,
    refetch: refetchPermissions
  } = useTeamPermissions(userId, token);

  // Load current permissions when component mounts or user changes
  useEffect(() => {
    if (availablePermissions?.userPermissions) {
      const userPerms = availablePermissions.userPermissions;
      setSelectedPermissions(userPerms);
      setOriginalPermissions(userPerms); // Store original for reset
      setHasChanges(false); // No changes initially
    }
  }, [availablePermissions]);

  // Check if there are changes
  useEffect(() => {
    const isChanged = JSON.stringify(selectedPermissions.sort()) !== JSON.stringify(originalPermissions.sort());
    setHasChanges(isChanged);
  }, [selectedPermissions, originalPermissions]);

  const handlePermissionChange = (permissionKey: string, checked: boolean) => {
    if (checked) {
      setSelectedPermissions(prev => [...prev, permissionKey]);
    } else {
      setSelectedPermissions(prev => prev.filter(p => p !== permissionKey));
    }
  };

  const handleSelectAllInGroup = (groupKey: string) => {
    const group = permissionGroups.find(g => g.key === groupKey);
    if (!group) return;

    const groupPermissionKeys = group.permissions.map(p => p.key);
    const allSelected = groupPermissionKeys.every(key =>
      selectedPermissions.includes(key)
    );

    if (allSelected) {
      // Deselect all in group
      setSelectedPermissions(prev =>
        prev.filter(key => !groupPermissionKeys.includes(key))
      );
    } else {
      // Select all in group
      setSelectedPermissions(prev => {
        const newPermissions = [...prev];
        groupPermissionKeys.forEach(key => {
          if (!newPermissions.includes(key)) {
            newPermissions.push(key);
          }
        });
        return newPermissions;
      });
    }
  };

  const handleSelectAll = () => {
    const allPermissionKeys = permissionGroups.flatMap(group =>
      group.permissions.map(p => p.key)
    );

    if (selectedPermissions.length === allPermissionKeys.length) {
      // Deselect all
      setSelectedPermissions([]);
    } else {
      // Select all
      setSelectedPermissions(allPermissionKeys);
    }
  };

  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${END_POINT.BASE_URL}/v1/team/member/${userId}/permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          permissions: selectedPermissions
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update permissions');
      }

      const data = await response.json();
      setOriginalPermissions([...selectedPermissions]);
      setHasChanges(false);

      await queryClient.refetchQueries([QUERY_KEY_FOR_TEAM_MEMBERS]);

      await refetchPermissions();

      message.success(data?.message || 'Permissions updated successfully');
      onSuccess?.();
    } catch (error: any) {
      console.error('Error updating permissions:', error);
      message.error(error.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSelectedPermissions(originalPermissions);
  };

  const handleCancel = () => {
    if (hasChanges) {
      // Ask for confirmation if there are unsaved changes
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        onSuccess?.();
      }
    } else {
      onSuccess?.();
    }
  };

  if (loadingPermissions) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spin size="large" tip="Loading team member permissions..." />
      </div>
    );
  }

  if (loadError) {
    return (
      <Alert
        message="Error"
        description="Failed to load team member permissions. Please try again."
        type="error"
        showIcon
      />
    );
  }

  if (!availablePermissions) {
    return (
      <Alert
        message="No Data"
        description="Unable to load team member information."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div className="space-y-6">
      <Alert
        message="Permission Management"
        description={
          <div>
            <p>Manage permissions for {availablePermissions.userName} ({availablePermissions.userEmail})</p>
            <p className="mt-1 text-sm text-gray-600">
              Currently has {originalPermissions.length} permission(s). Check/uncheck permissions as needed.
            </p>
          </div>
        }
        type="info"
        showIcon
        icon={<SecurityScanOutlined />}
      />

      {/* Quick Actions */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          <Button
            size="small"
            onClick={handleSelectAll}
            icon={<CheckOutlined />}
          >
            {selectedPermissions.length === permissionGroups.flatMap(g => g.permissions).length
              ? 'Deselect All'
              : 'Select All'
            }
          </Button>
          <Button
            size="small"
            onClick={handleReset}
            icon={<CloseOutlined />}
            disabled={!hasChanges}
          >
            Reset Changes
          </Button>
        </div>
        <div className="flex items-center space-x-3">
          <Tag color={hasChanges ? "orange" : "blue"}>
            {selectedPermissions.length} permissions selected
          </Tag>
          {hasChanges && (
            <Tag color="orange">Unsaved Changes</Tag>
          )}
        </div>
      </div>

      <Divider />

      {/* Permission Groups */}
      <div className="space-y-8">
        {permissionGroups.map(group => {
          const groupPermissionKeys = group.permissions.map(p => p.key);
          const selectedCount = groupPermissionKeys.filter(key =>
            selectedPermissions.includes(key)
          ).length;
          const allSelected = selectedCount === group.permissions.length;
          const someSelected = selectedCount > 0 && !allSelected;

          return (
            <Card
              key={group.key}
              title={
                <div className="flex items-center justify-between">
                  <div>
                    <Title level={5} className="mb-1">{group.label}</Title>
                    <Text type="secondary" className="text-sm">
                      {group.description}
                    </Text>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Tag color={allSelected ? 'green' : someSelected ? 'blue' : 'default'}>
                      {selectedCount}/{group.permissions.length}
                    </Tag>
                    <Button
                      size="small"
                      onClick={() => handleSelectAllInGroup(group.key)}
                    >
                      {allSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                </div>
              }
              className="shadow-sm"
            >
              <Row gutter={[16, 16]}>
                {group.permissions.map(permission => {
                  const hasPermission = selectedPermissions.includes(permission.key);
                  const originallyHad = originalPermissions.includes(permission.key);

                  return (
                    <Col xs={24} sm={12} md={8} key={permission.key}>
                      <Card
                        size="small"
                        className={`h-full border ${hasPermission
                            ? originallyHad ? 'border-blue-500 bg-blue-50' : 'border-green-500 bg-green-50'
                            : originallyHad ? 'border-red-500 bg-red-50' : ''
                          }`}
                      >
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            checked={hasPermission}
                            onChange={(e) =>
                              handlePermissionChange(permission.key, e.target.checked)
                            }
                            className="mt-1"
                          />
                          <div>
                            <div className="font-medium">{permission.label}</div>
                            <div className="text-sm text-gray-600 mt-1">
                              {permission.description}
                              {originallyHad && !hasPermission && (
                                <span className="ml-2 text-xs text-red-600">
                                  (Will be removed)
                                </span>
                              )}
                              {!originallyHad && hasPermission && (
                                <span className="ml-2 text-xs text-green-600">
                                  (Will be added)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          );
        })}
      </div>

      {/* Save Actions */}
      <Divider />
      <div className="flex justify-end space-x-4 pt-4">
        <Button
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleSavePermissions}
          loading={saving}
          disabled={saving || !hasChanges}
        >
          {saving ? 'Saving...' : 'Save Permissions'}
        </Button>
      </div>
    </div>
  );
};